import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingCart,
  Trash2,
  Minus,
  Plus,
  Share2,
  Users,
  Copy,
  Check,
  ShoppingBag,
  MessageCircle,
  Store,
  Truck,
  Zap,
  MapPin,
  Package,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCart, type CartItem, shippingOptions, type ShippingOptionId } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/* ─── Cart Item Row ─── */
const CartItemRow = ({ item }: { item: CartItem }) => {
  const { updateQuantity, removeItem } = useCart();

  return (
    <motion.div
      layout="position"
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 30 }}
      className="flex gap-3 py-3"
    >
      <div className="w-14 h-14 rounded-lg bg-surface-sunken border flex items-center justify-center text-2xl shrink-0">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt="" className="w-full h-full rounded-lg object-cover" />
        ) : (
          item.emoji
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold line-clamp-2 leading-relaxed">{item.name}</p>
        {item.customText && (
          <p className="text-xs text-muted-foreground line-clamp-1 leading-relaxed">
            הקדשה: {item.customText}
          </p>
        )}
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex items-center border rounded-lg">
            <button
              onClick={() => updateQuantity(item.id, item.quantity - 1)}
              className="px-2 py-1 hover:bg-muted transition-colors"
            >
              <Minus size={12} />
            </button>
            <span className="px-2.5 py-1 text-xs font-bold border-x">
              {item.quantity}
            </span>
            <button
              onClick={() => updateQuantity(item.id, item.quantity + 1)}
              className="px-2 py-1 hover:bg-muted transition-colors"
            >
              <Plus size={12} />
            </button>
          </div>
          <span className="text-sm font-bold text-primary">
            ₪{(item.price * item.quantity).toLocaleString()}
          </span>
        </div>
      </div>
      <button
        onClick={() => removeItem(item.id)}
        className="self-start p-1 text-muted-foreground hover:text-destructive transition-colors"
      >
        <Trash2 size={14} />
      </button>
    </motion.div>
  );
};

/* ─── Shipping Selector per Vendor ─── */
const shippingIcons: Record<ShippingOptionId, typeof Truck> = {
  express: Zap,
  pickup: MapPin,
};

const VendorShippingSelector = ({
  vendorId,
  vendorName,
  vendorSubtotal,
}: {
  vendorId: string;
  vendorName: string;
  vendorSubtotal: number;
}) => {
  const { vendorShipping, setVendorShipping } = useCart();
  const selected = vendorShipping[vendorId] || "express";
  const isFreeShipping = vendorSubtotal >= 500;

  return (
    <div className="bg-muted/40 rounded-lg p-2.5 space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold flex items-center gap-1.5">
          <Truck size={12} className="text-primary" />
          משלוח
        </p>
        {isFreeShipping && (
          <Badge className="bg-success/10 text-success text-[10px] px-1.5 py-0">
            משלוח חינם!
          </Badge>
        )}
      </div>
      <Select
        value={selected}
        onValueChange={(v) => setVendorShipping(vendorId, v as ShippingOptionId)}
      >
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {shippingOptions.map((opt) => {
            const Icon = shippingIcons[opt.id];
            return (
              <SelectItem key={opt.id} value={opt.id}>
                <span className="flex items-center gap-1.5">
                  <Icon size={12} />
                  {opt.label}
                  {isFreeShipping ? (
                    <span className="text-success font-bold mr-1">חינם</span>
                  ) : opt.price > 0 ? (
                    <span className="text-muted-foreground mr-1">₪{opt.price}</span>
                  ) : (
                    <span className="text-success font-bold mr-1">חינם</span>
                  )}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

/* ─── Vendor Group ─── */
const VendorGroup = ({
  vendorId,
  vendorName,
  items,
}: {
  vendorId: string;
  vendorName: string;
  items: CartItem[];
}) => {
  const vendorSubtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <motion.div
      layout="position"
      className="border rounded-xl overflow-hidden"
    >
      {/* Vendor header */}
      <div className="bg-muted/50 px-4 py-2.5 flex items-center gap-2 border-b">
        <Store size={14} className="text-primary" />
        <span className="text-sm font-bold flex-1">{vendorName}</span>
        <span className="text-xs text-muted-foreground">
          ₪{vendorSubtotal.toLocaleString()}
        </span>
      </div>

      {/* Items */}
      <div className="px-4 divide-y">
        <AnimatePresence>
          {items.map((item) => (
            <CartItemRow key={`${item.id}-${item.customText || ""}`} item={item} />
          ))}
        </AnimatePresence>
      </div>

      {/* Shipping selector */}
      <div className="px-4 pb-3">
        <VendorShippingSelector
          vendorId={vendorId}
          vendorName={vendorName}
          vendorSubtotal={vendorSubtotal}
        />
      </div>
    </motion.div>
  );
};

/* ─── Main Cart Sheet ─── */
const CartSheet = () => {
  const navigate = useNavigate();
  const {
    items, isOpen, setIsOpen, subtotal, itemCount, clearCart, isValid,
    shippingTotal, grandTotal,
  } = useCart();
  const { user } = useAuth();
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [groupOrder, setGroupOrder] = useState(false);

  // Group items by vendor
  const vendorGroups = useMemo(() => {
    const groups = new Map<string, { name: string; items: CartItem[] }>();
    for (const item of items) {
      const vid = item.vendorId || "_platform";
      const vname = item.vendorName || "SmileyGift";
      if (!groups.has(vid)) {
        groups.set(vid, { name: vname, items: [] });
      }
      groups.get(vid)!.items.push(item);
    }
    return groups;
  }, [items]);

  const handleShareCart = async () => {
    if (!user) {
      toast.error("יש להתחבר כדי לשתף את הסל");
      return;
    }
    if (items.length === 0) {
      toast.error("הסל ריק");
      return;
    }

    setSharing(true);
    try {
      const shareCode = Math.random().toString(36).substring(2, 10).toUpperCase();
      const taggedItems = items.map((item) => ({
        ...item,
        addedBy: user.id,
        addedByName: user.user_metadata?.display_name || user.email?.split("@")[0] || "יוצר הסל",
      }));
      const { error } = await supabase.from("shared_carts").insert({
        share_code: shareCode,
        creator_id: user.id,
        items: taggedItems as any,
        is_group_order: groupOrder,
      });
      if (error) throw error;

      const url = `${window.location.origin}/סל-משותף/${shareCode}`;
      setShareUrl(url);
      toast.success("קישור הסל נוצר בהצלחה!");
    } catch {
      toast.error("שגיאה ביצירת הקישור");
    } finally {
      setSharing(false);
    }
  };

  const handleCopyUrl = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const whatsappShare = () => {
    if (!shareUrl) return;
    const text = encodeURIComponent(
      groupOrder
        ? `🛒 הצטרפו להזמנה הקבוצתית שלי ב-SmileyGift!\n${shareUrl}`
        : `🎁 היי! תוכל/י לשלם עבורי? הנה הסל שלי ב-SmileyGift:\n${shareUrl}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-5 pt-5 pb-3 border-b">
          <SheetTitle className="flex items-center gap-2 text-right">
            <ShoppingCart size={20} className="text-primary" />
            הסל שלי
            {itemCount > 0 && (
              <Badge className="bg-primary text-primary-foreground">{itemCount}</Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <ShoppingBag size={48} className="text-muted-foreground/30 mb-4" />
            <p className="font-semibold text-muted-foreground">הסל ריק</p>
            <p className="text-sm text-muted-foreground mt-1">
              הוסף מוצרים כדי להתחיל
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setIsOpen(false)}
            >
              המשך לקנות
            </Button>
          </div>
        ) : (
          <>
            {/* Vendor-grouped items */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {[...vendorGroups.entries()].map(([vid, group], idx) => (
                <VendorGroup
                  key={vid}
                  vendorId={vid}
                  vendorName={`מחסן ${idx + 1}`}
                  items={group.items}
                />
              ))}
            </div>

            {/* Group Order Toggle */}
            <div className="px-5 py-3 border-t bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-primary" />
                  <div>
                    <p className="text-sm font-semibold">הזמנה קבוצתית</p>
                    <p className="text-[10px] text-muted-foreground">
                      אפשר לאחרים להצטרף להזמנה
                    </p>
                  </div>
                </div>
                <Switch checked={groupOrder} onCheckedChange={setGroupOrder} />
              </div>
              {groupOrder && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="mt-2 bg-primary/10 rounded-lg p-2.5 text-xs text-primary font-medium"
                >
                  🎉 מצב הזמנה קבוצתית פעיל! שתף את הסל כדי שחברים יוכלו להצטרף
                </motion.div>
              )}
            </div>

            {/* Share Cart */}
            <div className="px-5 py-3 border-t space-y-2">
              {!shareUrl ? (
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={handleShareCart}
                  disabled={sharing}
                >
                  <Share2 size={16} />
                  {sharing
                    ? "יוצר קישור..."
                    : groupOrder
                    ? "שתף הזמנה קבוצתית"
                    : "שלם עבורי (שתף סל)"}
                </Button>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div className="flex-1 bg-muted rounded-lg px-3 py-2 text-xs font-mono truncate" dir="ltr">
                      {shareUrl}
                    </div>
                    <Button size="sm" variant="outline" onClick={handleCopyUrl} className="shrink-0">
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </Button>
                  </div>
                  <Button
                    className="w-full gap-2 bg-success text-success-foreground hover:bg-success/90"
                    onClick={whatsappShare}
                  >
                    <MessageCircle size={16} />
                    שתף בוואטסאפ
                  </Button>
                </div>
              )}
            </div>

            <Separator />

            {/* Summary & Checkout */}
            <div className="px-5 py-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">סה״כ מוצרים</span>
                <span className="font-semibold">₪{subtotal.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Package size={12} />
                  משלוח ({vendorGroups.size} {vendorGroups.size === 1 ? "ספק" : "ספקים"})
                </span>
                <span className={`font-semibold ${shippingTotal === 0 ? "text-success" : ""}`}>
                  {shippingTotal === 0 ? "חינם!" : `₪${shippingTotal.toLocaleString()}`}
                </span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="font-bold">סה״כ לתשלום</span>
                <span className="text-xl font-black text-primary">
                  ₪{grandTotal.toLocaleString()}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">משלוח חינם בהזמנות מעל ₪500 לכל ספק</p>
              <Button
                size="lg"
                className="w-full gradient-primary text-primary-foreground font-bold gap-2 shine"
                disabled={!isValid || items.length === 0}
                onClick={() => {
                  setIsOpen(false);
                  setTimeout(() => navigate("/תשלום"), 350);
                }}
              >
                <ShoppingCart size={18} />
                לתשלום ₪{grandTotal.toLocaleString()}
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 text-xs text-muted-foreground"
                  onClick={() => setIsOpen(false)}
                >
                  המשך לקנות
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 text-xs text-destructive"
                  onClick={() => {
                    clearCart();
                    setShareUrl(null);
                  }}
                >
                  רוקן סל
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;
